import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthState } from "@/hooks/useAuthState";
import {
  canShowLiveSubscriptionPromo,
  isOwnerUser,
} from "@/lib/livePlanAccess";
import { UserPhotoService } from "@/services/userPhotoService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  Home,
  Handshake,
  Bell,
  MessageCircle,
  User,
  Calendar,
  FileText,
  Building2,
  Palmtree,
  BarChart3,
  Image,
  Sparkles,
  ChevronRight,
  Heart,
  Mail,
  CreditCard,
} from "lucide-react";

type MenuItem = {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  description: string;
};

type Section = {
  title: string;
  items: MenuItem[];
};

const UserArea = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [photoError, setPhotoError] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const {
    isAuthenticated,
    userType,
    userName,
    userPhoto,
    userId,
    user,
    subscriptionPlan,
    loading: authLoading,
  } = useAuthState();

  const userPhotoUrl =
    userPhoto && userId && !photoError
      ? UserPhotoService.getUserPhotoUrl(userId, userPhoto)
      : undefined;

  // Modal de upsell para owners que não são do plano Live
  useEffect(() => {
    if (
      !authLoading &&
      canShowLiveSubscriptionPromo(userType, subscriptionPlan, user)
    ) {
      const dismissed = sessionStorage.getItem("upsell_modal_dismissed");
      if (!dismissed) {
        setShowUpsellModal(true);
      }
    }
  }, [authLoading, userType, subscriptionPlan, user]);

  const handleDismissUpsell = () => {
    setShowUpsellModal(false);
    sessionStorage.setItem("upsell_modal_dismissed", "true");
  };

  if (authLoading || !isAuthenticated) {
    return null;
  }

  const getSections = (): Section[] => {
    if (userType === "admin") {
      return [
        {
          title: "Administração",
          items: [
            { id: "admin-dashboard", title: "Dashboard", icon: BarChart3, href: "/minha-area/admin/dashboard", description: "Visão geral do sistema" },
            { id: "admin-usuarios", title: "Usuários", icon: Users, href: "/minha-area/admin/usuarios", description: "Gerenciar usuários" },
            { id: "admin-imoveis", title: "Imóveis", icon: Home, href: "/minha-area/admin/imoveis", description: "Gerenciar imóveis" },
            { id: "admin-parceiros", title: "Parceiros", icon: Handshake, href: "/minha-area/admin/parceiros", description: "Gerenciar parceiros" },
            { id: "admin-resorts", title: "Resorts", icon: Palmtree, href: "/minha-area/admin/resorts", description: "Dashboard dos empreendimentos" },
            { id: "admin-empreendimentos", title: "Empreendimentos", icon: Building2, href: "/minha-area/admin/empreendimentos", description: "Gerenciar empreendimentos" },
            { id: "admin-banners", title: "Banners", icon: Image, href: "/minha-area/admin/banners", description: "Banners do site" },
            { id: "admin-contatos", title: "Contatos", icon: Mail, href: "/minha-area/admin/contatos", description: "Mensagens de contato" },
            { id: "admin-reservas", title: "Reservas", icon: Calendar, href: "/minha-area/admin/reservas", description: "Gerenciar reservas" },
          ],
        },
        {
          title: "Comunicação e conta",
          items: [
            { id: "admin-notificacoes", title: "Notificações", icon: Bell, href: "/minha-area/notificacoes", description: "Todas as notificações" },
            { id: "admin-chat", title: "Mensagens", icon: MessageCircle, href: "/minha-area/mensagens", description: "Conversas" },
            { id: "admin-meus-dados", title: "Meus Dados", icon: User, href: "/minha-area/cadastro", description: "Perfil administrativo" },
            { id: "admin-documentos", title: "Documentos", icon: FileText, href: "/minha-area/documentos", description: "Modelos de contratos" },
          ],
        },
      ];
    }

    const canManageProperties = userType === "owner" && (subscriptionPlan === "porto2life" || subscriptionPlan === "live");
    const sections: Section[] = [];

    const viagensItems: MenuItem[] = [
      { id: "reservas", title: "Minhas Reservas", icon: Calendar, href: "/minha-area/reservas", description: "Suas reservas confirmadas" },
    ];
    if (canManageProperties) {
      viagensItems.push({ id: "solicitacoes", title: "Solicitações de Reservas", icon: Calendar, href: "/minha-area/solicitacoes-reservas", description: "Solicitações recebidas" });
      viagensItems.push({ id: "meus-imoveis", title: "Meus Imóveis", icon: Home, href: "/minha-area/meus-imoveis", description: "Gerenciar imóveis" });
    }
    viagensItems.push({ id: "favoritos", title: "Favoritos", icon: Heart, href: "/minha-area/favoritos", description: "Imóveis favoritos" });
    sections.push({ title: "Viagens e imóveis", items: viagensItems });

    sections.push({
      title: "Comunicação",
      items: [
        { id: "mensagens", title: "Mensagens", icon: MessageCircle, href: "/minha-area/mensagens", description: "Conversas" },
        { id: "notificacoes", title: "Notificações", icon: Bell, href: "/minha-area/notificacoes", description: "Suas notificações" },
      ],
    });

    const contaItems: MenuItem[] = [
      { id: "cadastro", title: "Meus Dados", icon: User, href: "/minha-area/cadastro", description: "Perfil e configurações" },
    ];
    if (isOwnerUser(userType)) {
      contaItems.push({
        id: "minha-assinatura",
        title: "Minha assinatura",
        icon: CreditCard,
        href: "/minha-area/minha-assinatura",
        description: "Ver plano atual (Live, Porto 2 Life ou Essencial)",
      });
    }
    if (canManageProperties) {
      contaItems.push({ id: "documentos", title: "Documentos", icon: FileText, href: "/minha-area/documentos", description: "Modelos de contratos" });
    }
    sections.push({ title: "Conta", items: contaItems });

    return sections;
  };

  const sections = getSections();
  const showAssinarLive = canShowLiveSubscriptionPromo(
    userType,
    subscriptionPlan,
    user,
  );
  const displayName = userName ?? "Visitante";

  const handleCardClick = (href: string) => {
    navigate(href);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent, href: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(href);
    }
  };

  return (
    <>
      <main className="container mx-auto px-4 sm:px-6 max-w-6xl py-8 sm:py-10">
        {/* Welcome */}
        <header className="mb-10 sm:mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              {userPhotoUrl ? (
                <img
                  src={userPhotoUrl}
                  alt=""
                  className="h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover ring-2 ring-slate-200 dark:ring-slate-700"
                  onError={() => setPhotoError(true)}
                />
              ) : (
                <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-slate-200 dark:ring-slate-700">
                  <User className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
                </div>
              )}
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100">
                  Olá, {displayName}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Escolha onde deseja ir na sua área
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Assinar Live (destaque para proprietários freemium/porto2life) */}
        {showAssinarLive && (
          <section className="mb-10">
            <Card
              className="overflow-hidden border-2 border-orange-200 dark:border-orange-900/50 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/20 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-800"
              onClick={() => handleCardClick("/minha-area/assinar-live")}
              role="button"
              tabIndex={0}
              aria-label="Assinar plano Live"
              onKeyDown={(e) => handleCardKeyDown(e, "/minha-area/assinar-live")}
            >
              <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-orange-500 text-white shrink-0">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      Assinar plano Live
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                      R$ 9,90/mês ou R$ 59,90/ano — gerencie propriedades e reservas em todos os resorts
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600 text-white shrink-0 w-full sm:w-auto"
                  aria-hidden
                >
                  Conhecer plano Live
                </Button>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Sections */}
        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-sm font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">
                {section.title}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.items.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.href;
                  const isAdminItem = item.id.startsWith("admin-");

                  return (
                    <Card
                      key={item.id}
                      className={`group cursor-pointer transition-all duration-200 hover:shadow-md border-2 ${
                        isActive
                          ? "border-primary bg-primary/5 dark:bg-primary/10"
                          : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
                      }`}
                      onClick={() => handleCardClick(item.href)}
                      role="button"
                      tabIndex={0}
                      aria-label={`Ir para ${item.title}`}
                      onKeyDown={(e) => handleCardKeyDown(e, item.href)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start gap-4">
                          <div
                            className={`p-2.5 rounded-lg shrink-0 ${
                              isAdminItem
                                ? "bg-primary text-primary-foreground"
                                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                            }`}
                          >
                            <IconComponent className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <CardTitle className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center justify-between gap-2">
                              <span>{item.title}</span>
                              <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-primary shrink-0" />
                            </CardTitle>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {sections.length === 0 && (
          <p className="text-center text-muted-foreground py-12">
            Nenhum item de menu disponível no momento.
          </p>
        )}
      </main>

      {/* Modal de upsell para owners sem plano Live */}
      <Dialog open={showUpsellModal} onOpenChange={setShowUpsellModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-center">
              O Universo Live Stay acaba de expandir.
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            <p>
              Temos o prazer de anunciar a chegada de novos empreendimentos ao nosso portal.
              Preparamos uma estrutura completa para que você aproveite o melhor do mercado de multipropriedades:
            </p>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900/50">
                <p className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Para Proprietários:</p>
                <p>
                  Amplie o alcance do seu patrimônio. Com novos empreendimentos na rede, você ganha maior
                  liquidez para suas cotas e diversifica suas possibilidades de intercâmbio, conectando-se
                  a uma base de clientes muito mais ampla.
                </p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50">
                <p className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Para Viajantes:</p>
                <p>
                  Sua próxima memória inesquecível está logo ali. Agora, você conta com destinos selecionados
                  de Norte a Sul do país, garantindo conforto e a exclusividade que sua família merece.
                </p>
              </div>
            </div>
            <p className="text-center font-medium text-slate-800 dark:text-slate-200">
              Assine o Plano Live e assuma o controle das suas experiências. Mais do que um serviço,
              um passaporte para destinos extraordinários onde você é sempre o protagonista.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDismissUpsell}
            >
              Agora não
            </Button>
            <Button
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white gap-2"
              onClick={() => {
                handleDismissUpsell();
                navigate("/minha-area/assinar-live");
              }}
            >
              <Sparkles className="h-4 w-4" />
              Assinar plano Live
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserArea;
